/**
 * Per-page FAQ and Article schema payloads. Kept centrally so pages stay lean
 * and the JSON-LD content is easy to audit and update in one place.
 *
 * Usage inside a page:
 *   import { pageSchemas } from '../data/pageSchemas';
 *   <SchemaMarkup faqs={pageSchemas.trackSubmission.faqs} />
 */

export const pageSchemas = {
  trackSubmission: {
    faqs: [
      {
        question: 'What does each submission status mean?',
        answer: 'Pending means your submission has been received and queued for verification. Verifying means a specialist is actively checking the balance with the retailer. Approved means verification succeeded and your payout amount is confirmed. Paid means the money has been sent to your chosen payout method. Rejected means the card could not be verified — we always email the reason.',
      },
      {
        question: 'How long does the tracking process take?',
        answer: 'Most submissions move from pending to paid in 6–14 hours during business days. Digital cards from major brands like Amazon, Apple, Walmart, and Target usually finish in under 8 hours. Physical cards and less common brands may take up to 24 hours.',
      },
      {
        question: "Where do I find my reference number?",
        answer: 'Your reference number is included in the confirmation email we sent right after you submitted the card. Search your inbox for CashifyGCMart — it appears as a bold string near the top of the email. Both the reference number and the email you used to submit are required to look up your status.',
      },
      {
        question: 'What should I do if my status hasn\u2019t changed in 24 hours?',
        answer: 'If your submission has been sitting in the same status for more than 24 hours, use the live chat on any page or reply to the confirmation email. A real specialist — not a bot — will get back to you the same business day.',
      },
    ],
  },

  securityPage: {
    faqs: [
      {
        question: 'How does CashifyGCMart protect my data?',
        answer: 'Everything moves over HTTPS. Our databases are encrypted at rest. Internal access to customer records is logged and limited to the people who need it for their job. We also run regular security audits and vulnerability scans.',
      },
      {
        question: 'Do you store my gift card codes forever?',
        answer: 'No. Gift card codes and PINs are purged shortly after the card is verified and paid out. We only retain submission records as long as required by tax and anti-fraud laws, then automatically delete them.',
      },
      {
        question: 'Will you share my information with advertisers?',
        answer: 'No. We never sell your data to marketers, we don\u2019t share it with partners for advertising, and we don\u2019t let third-party analytics vendors see your submission content.',
      },
      {
        question: 'Do you need my banking password or SSN?',
        answer: 'No. We only collect the minimum information needed to pay you — your email, name, card details, and the payout destination. We do not collect your Social Security number or banking passwords.',
      },
    ],
  },

  refundPolicy: {
    faqs: [
      {
        question: 'When can I cancel a submission?',
        answer: 'You can cancel any submission at no cost before it is approved. Once the status moves to Approved or Paid, cancellation is no longer possible because the card has been verified and funds are already in transit.',
      },
      {
        question: 'What happens if the balance you verify is different from what I reported?',
        answer: 'We contact you with the correct amount and let you choose: accept the revised payout, or cancel the submission at no cost. We never quietly adjust payouts without telling you first.',
      },
      {
        question: 'Are there any hidden fees?',
        answer: 'No. The number you see on the rate calculator is exactly what we send you. There is no processing fee, no payout fee, and no account fee. Our margin comes from reselling the cards, not from fees on sellers.',
      },
      {
        question: 'How do I dispute a rejected submission?',
        answer: 'Reply to the rejection email with your reference number and any extra evidence (clearer photo, original receipt, retailer confirmation). A human specialist reviews every dispute within one business day.',
      },
    ],
  },

  rateCalculator: {
    faqs: [
      {
        question: 'How are your payout rates calculated?',
        answer: 'Rates are set by three things: retailer demand, card type (digital vs physical), and the balance. High-demand brands like Amazon and Apple pay the strongest rates because we can resell inventory quickly. Rates are updated in near real time, so the number you see on the calculator is exactly what you will receive.',
      },
      {
        question: 'Why do digital cards pay more than physical cards?',
        answer: 'Digital cards can be redeemed instantly and resold at zero handling cost. Physical cards involve shipping, storage, and manual processing, which pulls the payout rate down by 2–4 percentage points for the same brand.',
      },
      {
        question: 'Is the calculator quote binding?',
        answer: 'Yes. We lock your quote for 60 minutes. If the market moves against us during that window, you still get the number you were shown.',
      },
      {
        question: 'Does the calculator work for partial balances?',
        answer: 'Yes. Enter the current remaining balance, not the original face value. Our rate is calculated on the amount we can actually recover, so you always get the honest number up front.',
      },
    ],
  },

  gettingStarted: {
    faqs: [
      {
        question: 'What do I need before I submit my first gift card?',
        answer: 'Three things: the card itself (physical card or digital code + PIN), the current remaining balance (accurate balances get approved faster), and your payout details (PayPal email, Zelle handle, CashApp tag, or bank info). We never ask for passwords.',
      },
      {
        question: 'How does CashifyGCMart verify my card?',
        answer: 'Once you submit, our team checks the balance directly with the retailer. Digital Amazon, Apple, Target, and Walmart cards are usually instant. Physical cards are photographed on both sides and matched against the retailer\u2019s records.',
      },
      {
        question: 'Which payout method is fastest?',
        answer: 'PayPal and Zelle usually land within 10–30 minutes after approval. CashApp is almost as quick. Bank transfers take a bit longer because they rely on ACH. The payout rate is the same across all methods.',
      },
      {
        question: 'How can I get the highest possible payout?',
        answer: 'Submit high-demand brands (Amazon, Apple, Visa, Target, Walmart), upload the original purchase receipt when you have it, combine multiple cards in one submission to reduce overhead, and submit during weekdays for faster clearing.',
      },
    ],
  },

  acceptedGiftCards: {
    faqs: [
      {
        question: 'Which gift card brands does CashifyGCMart accept?',
        answer: 'We accept over 150 major U.S. retailer and restaurant gift cards, including Amazon, Walmart, Target, Costco, Best Buy, Apple, Google Play, Microsoft, Nintendo, PlayStation, Xbox, Visa, Mastercard, American Express, Home Depot, Lowe\u2019s, Starbucks, Chipotle, Macy\u2019s, Nordstrom, Nike, Netflix, Hulu, Disney+, Spotify, and many more.',
      },
      {
        question: 'Do you accept both physical and digital gift cards?',
        answer: 'Yes, both are accepted and paid at comparable rates. Digital cards clear faster because codes can be verified instantly. Physical cards need clear photos of the front, back, and scratched-off PIN area.',
      },
      {
        question: 'What are the minimum and maximum balances?',
        answer: 'We accept cards from $10 up to $2,000 per card. Higher balances can be split across multiple submissions. Balances under $10 may be paid at a slightly lower rate due to fixed processing costs.',
      },
      {
        question: 'What kinds of cards don\u2019t you accept?',
        answer: 'Expired cards, cards reported lost or stolen, cards from retailers going out of business, and store-specific promotional loyalty points cannot be processed. Submit anyway if you\u2019re unsure — our quote step is free and we\u2019ll tell you the same day.',
      },
    ],
  },

  giftCardValues: {
    faqs: [
      {
        question: 'What determines the resale value of a gift card?',
        answer: 'Five factors: brand demand (Amazon and Walmart resell instantly, regional brands slower), card format (digital pays more than physical), balance size (larger balances pay slightly more per dollar), market timing (post-holiday supply dips prices), and proof of purchase (an attached receipt often unlocks a small rate bonus).',
      },
      {
        question: 'How much is a $100 Amazon gift card worth in cash?',
        answer: 'A $100 digital Amazon card with receipt typically pays around $82–$85. A physical Amazon card without a receipt pays around $78–$81. Exact live rates appear on the rate calculator.',
      },
      {
        question: 'Do smaller or regional retailer gift cards pay less?',
        answer: 'Yes. Cards from niche or regional retailers resell more slowly, which pulls the rate down a few percentage points. A $200 card from a smaller regional retailer might pay 55–65% versus 82–85% for the same balance on Amazon.',
      },
      {
        question: 'Does uploading a receipt really increase my payout?',
        answer: 'Yes. Attaching a dated receipt reassures the fraud engine the card was legitimately purchased and unlocks a small rate bonus. If you still have the email or paper receipt, upload it — it almost always pays for itself.',
      },
    ],
  },

  compareRates: {
    faqs: [
      {
        question: 'How do I honestly compare gift card exchanges?',
        answer: 'Look at six things, not just the headline rate: hidden fees, payout speed, available payment methods, whether the quote is locked, quality of customer support, and public reputation. Platforms that hide any of these tend to deliver a worse effective payout than their advertised rate suggests.',
      },
      {
        question: 'Why is CashifyGCMart\u2019s quote the final number?',
        answer: 'Because our quote is a lock, not a starting bid. There\u2019s no processing fee, payout fee, or verification charge subtracted later. The amount you see on the rate calculator is the amount that hits your account.',
      },
      {
        question: 'How fast will I get paid compared to other platforms?',
        answer: 'Most CashifyGCMart payouts clear within 6–14 business hours. Some competitors hold funds for 7–10 days as a fraud buffer. Even a 1% higher rate from a slower competitor is usually not worth waiting three extra days.',
      },
      {
        question: 'What payment methods should I expect from a good exchange?',
        answer: 'PayPal, Zelle, CashApp, and bank transfer. Platforms that only offer store credit, crypto, or obscure prepaid cards offer a fictional rate, not a usable one.',
      },
    ],
  },

  safetyGuide: {
    faqs: [
      {
        question: 'Is it safe to sell gift cards online?',
        answer: 'Yes, with a reputable exchange. Verify three basics: the platform uses HTTPS end to end, publishes a physical address and support contact, and pays you before taking control of your card. CashifyGCMart does all three.',
      },
      {
        question: 'How do I spot a gift card scam?',
        answer: 'Warning signs include: pressure to act within minutes, requests to buy cards at a store and send codes to a stranger, platforms that hide fees in fine print, no published business address, and payout promises in obscure crypto or store credit only.',
      },
      {
        question: 'What should I do if I\u2019ve been scammed?',
        answer: 'Report immediately to reportfraud.ftc.gov, file a report with IC3 at ic3.gov if the loss was significant, and contact the issuing retailer — some stores can freeze a card within minutes of a fraud report. The faster you act, the higher your chance of recovery.',
      },
      {
        question: 'How does CashifyGCMart protect sellers?',
        answer: 'We publish our full rate table, lock quotes for 60 minutes, never cold-contact sellers, only offer reversible and auditable payout methods, and openly post our business registration, physical address, and support contact.',
      },
    ],
  },

  termsOfService: {
    article: {
      headline: 'CashifyGCMart Terms of Service',
      description: 'The full user agreement for selling gift cards on CashifyGCMart, written in plain English alongside the formal legal text.',
      path: '/terms-of-service',
      datePublished: '2024-01-01',
    },
  },

  privacyPolicy: {
    article: {
      headline: 'CashifyGCMart Privacy Policy',
      description: 'How CashifyGCMart collects, uses, retains, and protects personal data when you sell a gift card or use the website.',
      path: '/privacy-policy',
      datePublished: '2024-01-01',
    },
  },

  cookiePolicy: {
    article: {
      headline: 'CashifyGCMart Cookie Policy',
      description: 'Which cookies CashifyGCMart uses, why they are used, and how to control them in your browser.',
      path: '/cookie-policy',
      datePublished: '2024-01-01',
    },
  },

  seasonalTrends: {
    article: {
      headline: 'Seasonal Gift Card Rate Trends',
      description: 'How gift card resale rates fluctuate across holidays, back-to-school, and the summer gifting season — and when to sell for the best payout.',
      path: '/seasonal-trends',
      datePublished: '2024-01-01',
    },
  },
};
